import React, { useState, useEffect, Suspense, lazy, startTransition } from 'react';
// eslint-disable-next-line no-unused-vars
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { encodeId } from './utils/hashUtils';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { auth, db } from './firebaseConfig';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { Button } from '@mui/material';
import { HelmetProvider } from 'react-helmet-async';
import { SHUTDOWN_MODE } from './shutdownConfig';

import Navigation from './components/Navigation';
import ErrorBoundary from './components/ErrorBoundary';
import PrivateRoute from './components/PrivateRoute';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import SEO from './components/SEO';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import './darkThemeOverrides.css';

// Lazy load components
const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const Items = lazy(() => import('./pages/Items'));
const SignUp = lazy(() => import('./pages/SignUp'));
const SignIn = lazy(() => import('./pages/SignIn'));
const ItemDetail = lazy(() => import('./pages/ItemDetail'));
const Profile = lazy(() => import('./pages/Profile'));
const WebInbox = lazy(() => import('./pages/WebInbox'));
const Terms = lazy(() => import('./pages/Terms'));
const Guides = lazy(() => import('./pages/Guides'));
const Support = lazy(() => import('./pages/Support'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const LegalTerms = lazy(() => import('./pages/LegalTerms'));
const BuyScanTag = lazy(() => import('./pages/BuyScanTag'));
const Instructions = lazy(() => import('./pages/Instructions'));
const TermsAndConditions = lazy(() => import('./pages/TermsAndConditions'));
const Admin = lazy(() => import('./pages/Admin'));
const ContactUs = lazy(() => import('./pages/ContactUs'));
const MaintenancePage = lazy(() => import('./pages/MaintenancePage'));
const ShutdownPage = lazy(() => import('./pages/ShutdownPage'));
const EmailVerification = lazy(() => import('./pages/EmailVerification'));
const ProductPage = lazy(() => import('./pages/ProductPage'));
const Reviews = lazy(() => import('./pages/Reviews'));

// Loading component for Suspense fallback
const LoadingFallback = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    background: 'linear-gradient(135deg, #0396FF 0%, #ABDCFF 100%)'
  }}>
    <div style={{
      background: 'rgba(255, 255, 255, 0.9)',
      padding: '2rem',
      borderRadius: '16px',
      boxShadow: '0 8px 32px 0 rgba(3, 150, 255, 0.2)',
      textAlign: 'center'
    }}>
      <div className="loading-spinner"></div>
      <p style={{ marginTop: '1rem', color: '#1976D2' }}>Loading...</p>
    </div>
  </div>
);

// Utility function for data sanitization
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  return DOMPurify.sanitize(input.trim());
};

// Remove console logs in production
if (process.env.NODE_ENV === 'production') {
  console.log = () => {};
  console.error = () => {};
  console.debug = () => {};
}

// Legacy URL redirect component
const LegacyItemRedirect = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const hashedId = encodeId(id);
    if (window.location.pathname !== `/item/${hashedId}`) {
      navigate(`/item/${hashedId}`, { replace: true });
    }
  }, [id, navigate]);

  return <LoadingFallback />;
};

function AppContent() {
  const [lastActivity, setLastActivity] = useState(Date.now());
  const { currentUser } = useAuth();
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState(null);
  const location = useLocation();

  // Combined effect for maintenance mode and admin status
  useEffect(() => {
    let isMounted = true;
    let unsubscribe = () => {};
    
    const initialize = async () => {
      try {
        // Check admin status if user exists
        if (currentUser?.uid) {
          const userRef = doc(db, 'users', currentUser.uid);
          try {
            const userDoc = await getDoc(userRef);
            if (isMounted) {
              setIsAdmin(userDoc.exists() && userDoc.data().role === 'admin');
            }
          } catch (error) {
            console.error('Error checking admin status:', error);
            if (isMounted) {
              setIsAdmin(false);
              setError('Error checking admin status');
            }
          }
        } else if (isMounted) {
          setIsAdmin(false);
        }

        // Set up maintenance mode listener with error handling
        try {
          const maintenanceRef = doc(db, 'settings', 'maintenance');
          unsubscribe = onSnapshot(
            maintenanceRef, 
            (doc) => {
              if (isMounted) {
                if (doc.exists()) {
                  const data = doc.data();
                  setMaintenanceMode(data.enabled || false);
                  setMaintenanceMessage(data.message || '');
                }
                setLoading(false);
                setError(null);
              }
            },
            (error) => {
              console.error('Error checking maintenance mode:', error);
              if (isMounted) {
                setMaintenanceMode(false);
                setLoading(false);
                setError('Error checking maintenance status');
              }
            }
          );
        } catch (error) {
          console.error('Error setting up maintenance listener:', error);
          if (isMounted) {
            setMaintenanceMode(false);
            setLoading(false);
            setError('Error connecting to service');
          }
        }
      } catch (error) {
        console.error('Error during initialization:', error);
        if (isMounted) {
          setLoading(false);
          setError('Error initializing application');
        }
      }
    };

    initialize();

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [currentUser?.uid]);

  // Session timeout handling (disabled during shutdown)
  useEffect(() => {
    if (!currentUser || SHUTDOWN_MODE) return;
    
    const inactivityTimeout = 30 * 60 * 1000;
    
    const resetTimer = () => {
      startTransition(() => {
        setLastActivity(Date.now());
      });
    };

    const checkInactivity = () => {
      if (Date.now() - lastActivity > inactivityTimeout) {
        auth.signOut().then(() => {
          startTransition(() => {
            window.location.href = '/signin?timeout=true';
          });
        }).catch(error => {
          console.error('Error signing out:', error);
        });
      }
    };

    const activities = ['mousemove', 'keypress', 'click', 'touchstart', 'scroll'];
    activities.forEach(activity => {
      window.addEventListener(activity, resetTimer);
    });
    
    const interval = setInterval(checkInactivity, 60000);

    return () => {
      activities.forEach(activity => {
        window.removeEventListener(activity, resetTimer);
      });
      clearInterval(interval);
    };
  }, [lastActivity, currentUser]);

  if (loading) {
    return <LoadingFallback />;
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        padding: '20px',
        textAlign: 'center',
        background: 'linear-gradient(135deg, #0396FF 0%, #ABDCFF 100%)'
      }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          padding: '2rem',
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(3, 150, 255, 0.2)'
        }}>
          <h2>Connection Error</h2>
          <p>{error}</p>
          <p>Please try refreshing the page or contact support if the issue persists.</p>
          <Button
            onClick={() => window.location.reload()}
            variant="contained"
            sx={{ mt: 2 }}
          >
            Refresh Page
          </Button>
        </div>
      </div>
    );
  }

  // Special handling for admin route during maintenance mode
  const isAdminRoute = location.pathname === '/admin';
  
  // Define domains that should bypass maintenance mode
  const maintenanceExemptDomains = [
    'localhost',
    '127.0.0.1',
    'staging.myscantag.com',
    'dev.myscantag.com'  // Add more domains as needed
  ];
  
  const isMaintenanceExempt = maintenanceExemptDomains.some(domain => 
    window.location.hostname === domain || window.location.hostname.includes(domain)
  );

  if (maintenanceMode && !isMaintenanceExempt) {
    if (isAdminRoute) {
      if (!currentUser) {
        return <SignIn adminAccess={true} />;
      } else if (!isAdmin) {
        return <MaintenancePage message={maintenanceMessage} />;
      }
    } else if (!isAdmin) {
      return <MaintenancePage message={maintenanceMessage} />;
    }
  }

  if (SHUTDOWN_MODE) {
    return (
      <ErrorBoundary>
        <Navigation />
        <ScrollToTop />
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/item/:hash" element={<ItemDetail />} />
            <Route path="/items/:id" element={<LegacyItemRedirect />} />
            <Route path="/signin" element={<SignIn />} />
            <Route path="/admin" element={
              <PrivateRoute>
                <Admin />
              </PrivateRoute>
            } />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/legal" element={<LegalTerms />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="*" element={<ShutdownPage />} />
          </Routes>
        </Suspense>
        <Footer />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <Navigation />
      <ScrollToTop />
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/items" element={
            <PrivateRoute>
              <Items />
            </PrivateRoute>
          } />
          <Route path="/profile" element={
            <PrivateRoute>
              <Profile />
            </PrivateRoute>
          } />
          <Route path="/inbox" element={
            <PrivateRoute>
              <WebInbox />
            </PrivateRoute>
          } />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/signin" element={<SignIn />} />
          <Route path="/auth/emailverification" element={<EmailVerification />} />
          <Route path="/item/:hash" element={<ItemDetail />} />
          <Route path="/items/:id" element={<LegacyItemRedirect />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/guides" element={<Guides />} />
          <Route path="/support" element={<Support />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/legal" element={<LegalTerms />} />
          <Route path="/buy" element={<BuyScanTag />} />
          <Route path="/product" element={<ProductPage />} />
          <Route path="/reviews" element={<Reviews />} />
          <Route path="/instructions" element={<Instructions />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="/admin" element={
            <PrivateRoute>
              <Admin />
            </PrivateRoute>
          } />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      <Footer />
    </ErrorBoundary>
  );
}

function App() {
  return (
    <HelmetProvider>
      <Router>
        <AuthProvider>
          <SEO 
            title="ScanTag - Smart QR Tag Management System"
            description="Protect your valuables with ScanTag's QR code tags. Our secure lost and found system helps recover your items when they're lost."
            keywords="scantag, myscantag, qr code, lost and found, item recovery, lost items, found items, qr tag, smart tag"
          />
          <AppContent />
          <PWAInstallPrompt />
        </AuthProvider>
      </Router>
    </HelmetProvider>
  );
}

export default App; 